import React, { useState, useEffect } from 'react';
import { Result, Layout, Typography, Space } from 'antd';
import { useQuery, useMutation } from 'react-query';
import { Navigate } from 'react-router-dom';

import { useGlobals } from '../../../globals-context';
import { location } from '../../../env/browser';
import createQueryConfig from '../../../queries';
import useMerchantSettings from '../../../hooks/use-merchant-settings';
import { t } from '../../../utils/i18n';

const iframeStyle = {
  flex: 1,
  height: '100%',
  border: 'unset',
};

const CHECKOUT_SUCCESS_REDIRECT_URL = `${(location as any)?.origin}/success-checkout`;

function Checkout({ merchantId, locale, token }: any) {
  const [isLoading, toggleLoading] = useState(true);
  const [checkoutUrlResponse, setCheckoutUrlResponse] = useState(null);
  const { apiClient } = useGlobals() as any;

  function createCheckoutSession() {
    return apiClient.subscriptionV3.createCheckoutSession(
      {
        merchantId,
        locale,
        productFamily: 'shore_bm',
        redirectUrl: CHECKOUT_SUCCESS_REDIRECT_URL,
        metadata: {
          app: 'calendar-web',
          channel: 'self-service',
        },
      },
      token
    );
  }

  function handleCheckoutUrlResponse(response: any) {
    setCheckoutUrlResponse(response);
    toggleLoading(false);
  }

  const { mutate: createCheckoutLink } = useMutation(createCheckoutSession, {
    retry: 3,
    retryDelay: 3000,
    onSuccess: (res: any) => {
      res
        .json()
        .then(({ checkout_url: checkoutUrl }: any) => {
          handleCheckoutUrlResponse([false, checkoutUrl]);
        })
        .catch(() => {
          handleCheckoutUrlResponse([true, null]);
        });
    },
    onError: (error) => {
      console.error('Failed to create checkout session: ', error);

      handleCheckoutUrlResponse([true, null]);
    },
  });

  useEffect(() => {
    createCheckoutLink();
  }, [createCheckoutLink]);

  if (isLoading) {
    return null;
  }

  const [error, checkoutUrl] = checkoutUrlResponse as any;

  if (error) {
    return <p>Failed to load subscription checkout</p>;
  }

  return (
    <iframe
      title="Shore's subscription checkout"
      style={iframeStyle}
      src={checkoutUrl}
    />
  );
}

export function SubscriptionCheckout() {
  const { isLoading: isLoadingMerchantSettings, merchantSettings } =
    useMerchantSettings();
  const { queryClient, apiClient } = useGlobals() as any;
  const queryConfig = createQueryConfig(queryClient, apiClient);
  const { data: token, status: tokenQueryStatus } = useQuery(
    queryConfig.authToken()
  );
  const { status: subscriptionQueryStatus, data: subscription } = useQuery(
    queryConfig.subscription(merchantSettings)
  ) as any;

  const isLoading =
    isLoadingMerchantSettings ||
    [subscriptionQueryStatus, tokenQueryStatus].includes('idle') ||
    [subscriptionQueryStatus, tokenQueryStatus].includes('loading');

  if (isLoading) {
    return null;
  }

  if (tokenQueryStatus === 'error') {
    return <p>Session expired</p>;
  }

  if (subscriptionQueryStatus === 'error') {
    return (
      <p>Failed to get subscription information. Please try again later.</p>
    );
  }

  const { merchantId, locale } = merchantSettings;
  const subscriptionPartner = subscription?.customer?.partner;

  const isHeroldPartner = subscriptionPartner?.toLowerCase() === 'herold';

  if (isHeroldPartner) {
    if (subscription.status === 'active') {
      return <Navigate to="/" />;
    }

    if (['in_trial', 'cancelled'].includes(subscription.status)) {
      return (
        <Layout>
          <Layout.Content>
            <Result
              status="warning"
              title={
                <Space direction="vertical">
                  <Typography.Title level={4}>
                    {t('subscription-checkout.title')}
                  </Typography.Title>
                  <Typography.Text>
                    {t('subscription-checkout.description')}
                  </Typography.Text>
                </Space>
              }
            />
          </Layout.Content>
        </Layout>
      );
    }
  }

  if (subscription.status === 'active') {
    return <Navigate to="/settings/subscription" />;
  }

  return <Checkout merchantId={merchantId} locale={locale} token={token} />;
}
